"use client";
import { baseUrl, localUrl, WRITING_SERVICE_SUBDOMAIN } from "@/const";

import { usePathname } from "next/navigation";
import { useAppSelector } from "@/store";
import { isWritingServiceSelector } from "@/store/global/globalSlice";
import moment from "moment";
import { getMetaTitleDescription } from "@/utils/getMetaTitleDescription";
import { BreadcrumbsProps } from "@/components/shared/Breadcrumbs";
import { BreadcrumbJsonLd } from "next-seo";

type StructuredDataProps = {
  title: string;
  description: string;
  isCollectionPage?: boolean;
  isArticle?: boolean;
  dateUpdated?: string;
  datePublished?: string;
  h1?: string;
  breadcrumbs?: BreadcrumbsProps;
};

export const StructuredData = ({
  title,
  description,
  isCollectionPage,
  isArticle,
  dateUpdated,
  datePublished,
  h1,
  breadcrumbs,
}: StructuredDataProps) => {
  const url = usePathname();

  const { pageDescription, pageTitle } = getMetaTitleDescription(url, title, description);

  const isWritingService = useAppSelector(isWritingServiceSelector);

  const hasSearchInput = !isWritingService && (url === "/" || url.startsWith("/search"));

  const urlWithoutQueryParams = url.split("?")[0];

  const urlPrefix = localUrl
    ? `http://${localUrl}`
    : isWritingService
    ? `https://${WRITING_SERVICE_SUBDOMAIN}.${baseUrl}`
    : `https://${baseUrl}`;

  const getOrganization = (baseUrl: string) => ({
    "@context": "http://schema.org",
    "@type": "Organization",
    id: `https://${baseUrl}/#organization`,
    name: "StudyBounty",
    url: `https://${baseUrl}/`,
    // sameAs: ["https://www.instagram.com/study.crumb/", "https://www.facebook.com/studycrumb"],
    address: {
      "@type": "PostalAddress",
      addressLocality: "Tallinn Harju county, Estonia",
      postalCode: "10130",
      streetAddress: "Rüütli tn 24/1",
      email: "support@studybounty.com",
    },
    contactPoint: [
      {
        "@type": "ContactPoint",
        telephone: ["+15854380231"],
        contactType: "customer service",
      },
    ],
    logo: [
      {
        "@type": "ImageObject",
        id: `https://${baseUrl}/#logo`,
        inLanguage: "en-US",
        url: `https://${baseUrl}/img/logo.svg`,
        contentUrl: `https://${baseUrl}/img/logo.svg`,
        width: 121,
        height: 34,
      },
    ],
  });

  const getWebPage = (baseUrl: string) => {
    const data: Record<string, any> = {
      "@context": "http://schema.org",
      "@type": isCollectionPage ? "CollectionPage" : "WebPage",
      id: `https://${baseUrl}${urlWithoutQueryParams}/#webpage`,
      url: `https://${baseUrl}${urlWithoutQueryParams}`,
      name: pageTitle,
      description: pageDescription,
      inLanguage: "en-US",
      isPartOf: {
        "@type": "WebSite",
        id: `https://${baseUrl}/#website`,
        url: `https://${baseUrl}`,
        name: "StudyBounty",
        description:
          "StudyBounty is an academic writing platform designed by former students to help current students with their writing assignments.",
        publisher: {
          id: `https://${baseUrl}/#organization`,
        },
      },
    };

    if (hasSearchInput) {
      data.potentialAction = {
        "@type": "SearchAction",
        target: `https://${baseUrl}/search?title={s}`,
        "query-input": {
          "@type": "PropertyValueSpecification",
          valueRequired: true,
          valueMinlength: 3,
          valueMaxlength: 100,
          valueName: "s",
        },
      };
    }

    return data;
  };

  const getCreativeWork = (baseUrl: string) => ({
    "@context": "http://schema.org",
    "@type": "CreativeWork",
    name: h1,
    description: pageDescription,
    inLanguage: "en-US",
    datePublished: datePublished,
    dateModified: dateUpdated,
    publisher: {
      "@type": "Organization",
      name: "StudyBounty",
      url: `https://${baseUrl}`,
    },
    author: {
      "@type": "Organization",
      name: "StudyBounty",
      url: `https://${baseUrl}`,
    },
    image: {
      "@type": "ImageObject",
      url: `https://${baseUrl}/img/thumbnail.png`,
      encodingFormat: "image/png",
      width: 600,
      height: 360,
    },
    audience: {
      "@type": "EducationalAudience",
      educationalRole: ["teacher", "student"],
    },
    copyrightHolder: {
      "@type": "Organization",
      name: "StudyBounty",
      url: `https://${baseUrl}`,
    },
    copyrightYear: moment(datePublished).year(),
    educationalUse: ["study", "assignment"],
    genre: "Essay",
    learningResourceType: "study guide",
    url: `https://${baseUrl}${urlWithoutQueryParams}`,
  });

  const actualBaseUrl = isWritingService ? `${WRITING_SERVICE_SUBDOMAIN}.${baseUrl}` : String(baseUrl);

  return (
    <>
      {url === "/" && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(getOrganization(actualBaseUrl)),
          }}
          key="organization"
        />
      )}

      {isArticle ? (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(getCreativeWork(actualBaseUrl)),
          }}
          key="creativeWork"
        />
      ) : (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(getWebPage(actualBaseUrl)),
          }}
          key="webPage"
        />
      )}
      {breadcrumbs && (
        <BreadcrumbJsonLd
          useAppDir
          itemListElements={breadcrumbs.list.map((item) => {
            return {
              position: item.position,
              item: urlPrefix + item.href,
              name: item?.name?.replace(/"/g, '\\"'),
            };
          })}
        />
      )}
    </>
  );
};
